/**
 * SSE 流解析工具
 *
 * 提供通用的 SSE (Server-Sent Events) 流解析功能，
 * 从 agentTeamApi.ts 提取的通用逻辑。
 */

export interface SSEEvent {
  type: string;
  data: Record<string, unknown>;
}

export class SSEError extends Error {
  constructor(
    message: string,
    public status?: number
  ) {
    super(message);
    this.name = 'SSEError';
  }
}

export async function* createSSEStream(
  url: string,
  body: unknown,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'include',
    signal,
  });

  if (!response.ok) {
    let errorMessage = '请求失败';
    try {
      const data = await response.json();
      errorMessage = data.detail || data.message || errorMessage;
    } catch {
      // ignore
    }
    throw new SSEError(errorMessage, response.status);
  }

  if (!response.body) {
    throw new SSEError('响应体为空');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      let currentEvent = '';
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
          continue;
        }

        if (line.startsWith('data: ')) {
          const dataStr = line.slice(6).trim();
          try {
            const data = JSON.parse(dataStr);
            yield {
              type: currentEvent || data.type || 'message',
              data,
            };
            currentEvent = '';
          } catch {
            // ignore parse errors
          }
        }
      }
    }

    if (buffer.trim()) {
      const lines = buffer.split('\n');
      let currentEvent = '';
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
          continue;
        }
        if (line.startsWith('data: ')) {
          const dataStr = line.slice(6).trim();
          try {
            const data = JSON.parse(dataStr);
            yield {
              type: currentEvent || data.type || 'message',
              data,
            };
          } catch {
            // ignore parse errors
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
